import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { Button, ButtonVariants } from '@xing-com/button';
import { JobBookmarkState } from '@xing-com/crate-common-graphql-types';
import type { JobDetails } from '@xing-com/crate-jobs-helpers';
import { useJobBookmarkToggle } from '@xing-com/crate-jobs-hooks';
import { IconBookmark, IconBookmarkFilled } from '@xing-com/icons';
import { mediaConfined } from '@xing-com/layout-tokens';

import { useJobBookmarkStatusBanners } from '../../../hooks';

const StyledButton = styled(Button)<{ $isWide: boolean }>`
  width: ${({ $isWide }) => ($isWide ? '100%' : 'auto')};
  white-space: nowrap;
  padding: 0;

  @media ${mediaConfined} {
    flex-basis: ${({ $isWide }) => ($isWide ? '23%' : 'auto')};
  }
`;

type Props = {
  bookmarkState?: JobBookmarkState;
  job: JobDetails;
  iconOnly?: boolean;
  size?: 'small' | 'medium';
  sizeConfined?: 'small' | 'medium';
  variant?: ButtonVariants;
};

export const BookmarkButton: React.FC<Props> = ({
  bookmarkState,
  job,
  iconOnly = false,
  size = 'medium',
  sizeConfined,
  variant = 'secondary',
}) => {
  const { formatMessage } = useIntl();
  const isBookmarked =
    !!bookmarkState && bookmarkState !== JobBookmarkState.NotBookmarked;
  const { onCompleted, onError } = useJobBookmarkStatusBanners(isBookmarked);
  const onBookmark = useJobBookmarkToggle({
    jobId: job.id,
    isBookmarked,
    nwtTrackingOptions: {
      eventSchema: 'basic',
      page: `jobs/detail${job.redirectsToThirdPartyUrl ? '_external' : ''}`,
    },
    onCompleted,
    onError,
  });

  const handleOnBookmarkClick = (e: MouseEvent): void => {
    e.preventDefault();
    e.stopPropagation();

    onBookmark();
  };

  const buttonCopyId = isBookmarked
    ? 'JOBS_JOB_TEASER_SAVED'
    : 'JOBS_JOB_TEASER_SAVE';

  return (
    <StyledButton
      size={size}
      sizeConfined={sizeConfined}
      variant={variant}
      data-testid={
        isBookmarked
          ? 'job-details-unbookmark-button'
          : 'job-details-bookmark-button'
      }
      icon={isBookmarked ? IconBookmarkFilled : IconBookmark}
      // @ts-expect-error FIXME: SC6
      onClick={handleOnBookmarkClick}
      $isWide={!iconOnly}
      aria-label={formatMessage({ id: buttonCopyId })}
    >
      {!iconOnly ? <FormattedMessage id={buttonCopyId} /> : null}
    </StyledButton>
  );
};
